import React, { Fragment, ReactElement } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { IntlShape, useIntl } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';

// Action Creators / Selectors

import { changeData } from '@actions/ssap/ssapActionCreators';
import { getIsState, getSsapData, getStateCode } from '@reducers/appReducer';

// Utils

import { currencyTools } from '@utils/tools/currencyTools';
import { getFrequencyId } from '@utils/householdMember/getFrequencyId';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';

// Constants

import { INCOME_TYPES } from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { FREQUENCY_TYPES } from '@constants/ssap/hhmTypes/frequencyTypes';
import states from '@constants/configs/states';

// Types

import {
    DropdownOption,
    DropdownOptions,
    HouseholdMember,
    Income,
    IncomeDifferenceReason,
    OnChangeArgTypes,
    SsapData,
    SsapPage,
} from '@components/ssap/SsapTypes';
import { IEX_STATE_CODES } from '@constants/configs/states';


const Page3_38_E4 = (props: SsapPage): ReactElement => {

    const { DUMMY, JOB } = INCOME_TYPES;

    const {
        formName
    } = props;

    const dispatch: Dispatch<any> = useDispatch();
    const intlApi: IntlShape = useIntl();

    const data: SsapData = useSelector(getSsapData);
    const stateCode: IEX_STATE_CODES = useSelector(getStateCode);
    const isState = useSelector(getIsState);

    const {
        taxHousehold: {
            householdMember: householdMembers
        }
    } = data;

    const onTaxHouseholdReasonChange = (...args: OnChangeArgTypes): void => {
        const [ updatedHhm, hhmIndex ] = args;
        const newData: SsapData = { ...data };

        newData.taxHousehold.householdMember[hhmIndex] = updatedHhm;

        dispatch(changeData(newData));
    };

    const getIncomeDiscrepancyFilteredOptions = (
        dropdownOptions: DropdownOptions,
        stateCode: IEX_STATE_CODES,
        frequency
    ): DropdownOptions => {
        const {
            GEORGIA,
            IDAHO,
            ILLINOIS,
            GETINSURED,
            OREGON,
            NEVADA,
            PENNSYLVANIA,
            MINNESOTA,
            VIRGINIA
        } = states;

        const {
            MONTHLY,
            YEARLY
        } = FREQUENCY_TYPES;

        const supportedStates = [
            GEORGIA,
            IDAHO,
            ILLINOIS,
            GETINSURED,
            MINNESOTA,
            OREGON,
            NEVADA,
            PENNSYLVANIA,
            VIRGINIA
        ];

        const supportedFrequencies = [
            MONTHLY,
            YEARLY
        ];

        const hasDiscrepancyFilter: boolean = frequency
            ? supportedStates.includes(stateCode) && supportedFrequencies.includes(frequency.toUpperCase())
            : false;

        const filterOptions = (option: DropdownOption) => {
            switch (option.value) {
                case IncomeDifferenceReason.ENTERED_CORRECTLY:
                    return !hasDiscrepancyFilter;
                case IncomeDifferenceReason.TAX_FILING_CHANGE:
                case IncomeDifferenceReason.UNEMPLOYMENT:
                    return stateCode === MINNESOTA;
                default:
                    return true;
            }
        };

        return dropdownOptions.filter(
            (option: DropdownOption): boolean => filterOptions(option)
        );
    };

    const onIndividualReasonChange = (...args: OnChangeArgTypes): void => {
        const { indexInArray: [ householdMemberIndex, incomeIndex ], name, value } = parseFieldChangeArgs(args);

        const newData = { ...data };

        newData.taxHousehold.householdMember =
            newData.taxHousehold.householdMember.map((hhm: HouseholdMember, idx: number): HouseholdMember => {
                return idx === householdMemberIndex
                    ? {
                        ...hhm,
                        incomes: hhm.incomes.map((income: Income, idx: number): Income => {
                            return idx === incomeIndex
                                ? {
                                    ...income,
                                    [ name ]: value
                                }
                                : income;
                        })
                    }
                    : hhm;
            });

        dispatch(changeData(newData));
    };

    const getIndividualIncomeDiscrepancies = (): Array<ReactElement> => {
        interface ExtendedAndModifiedIncome extends Omit<Income, 'amount' | 'frequency'> {
            amount: string,
            frequency: string | null,
            incomeIndex: number,
            householdMemberIndex: number,
            personName: string
        }

        const incomesWithDiscrepancies: Array<ExtendedAndModifiedIncome> = data
            .taxHousehold
            .householdMember
            .reduce(
                (
                    matchedIncomes: Array<never>,
                    hhm: HouseholdMember,
                    householdMemberIndex: number
                ): Array<ExtendedAndModifiedIncome> => {
                    const { name } = hhm;

                    return [
                        ...matchedIncomes,
                        ...hhm
                            .incomes
                            .reduce(
                                (
                                    incomes: Array<never>,
                                    income: Income,
                                    incomeIndex: number
                                ): Array<ExtendedAndModifiedIncome>  => {

                                    const {
                                        amount,
                                        frequency,
                                        type
                                    } = income;

                                    const formattedAmount: string = currencyTools.getMoneyFormat(amount, true);
                                    const getFormattedFrequency = (): string | null => {
                                        return frequency === FREQUENCY_TYPES.UNSPECIFIED
                                            ? null
                                            : intlApi
                                                .formatMessage({ id: getFrequencyId(frequency) })
                                                .toLowerCase();
                                    };

                                    const personName: string = normalizeNameOfPerson(name);

                                    return (type === DUMMY || type === JOB) && !!income.jobIncomeExplanationRequiredIndicator
                                        ? [
                                            ...incomes,
                                            {
                                                ...income,
                                                incomeIndex,
                                                householdMemberIndex,
                                                amount: formattedAmount,
                                                frequency: getFormattedFrequency(),
                                                personName
                                            }
                                        ]
                                        : incomes;
                                },
                                [ ]
                            )
                    ];
                },
                [ ]
            );

        return incomesWithDiscrepancies.map((income: ExtendedAndModifiedIncome): ReactElement => {

            const {
                amount,
                frequency,
                householdMemberIndex,
                incomeDifferenceReason,
                incomeIndex,
                personName,
                sourceName
            } = income;

            return (
                <Fragment key = { `${income.incomeIndex}_${income.householdMemberIndex}` }>
                    <UniversalInput
                        legend = { {
                            legendText: 'ssap.input.legend.individual_income_discrepancy',
                            dataInstructions: 'instructional_text.income_discrepancy',
                            legendTextReplacements: {
                                amount: <strong>{ amount }</strong>,
                                frequency: <strong>{ frequency }</strong>,
                                personName: <strong>{ personName }</strong>,
                                sourceName
                            }
                        } }
                        label = { {
                            ulClass: [ 'margin-b-20' ],
                            showRequiredIcon: true
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            name: 'incomeDifferenceReason',
                            value: incomeDifferenceReason,
                            options: getIncomeDiscrepancyFilteredOptions(
                                DROPDOWN_OPTIONS.INCOME_INDIVIDUAL_DISCREPANCIES,
                                stateCode,
                                frequency
                            ),
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: income,
                            fields: []
                        } }
                        inputActions = { {
                            onFieldChange: onIndividualReasonChange
                        } }
                        indexInArray = { [ householdMemberIndex, incomeIndex ] }
                    />
                </Fragment>
            );
        });
    };

    const getTaxHouseholdDiscrepancies = (): ReactElement[] => {

        return householdMembers
            .reduce(
                (hhmsWithTaxHouseholdDiscrepancies, hhm: HouseholdMember, idx: number): ReactElement[] => {
                    const {
                        annualTaxIncome: {
                            incomeExplanationRequiredIndicator,
                            reportedIncome,
                            taxHouseholdIncomeDifferenceReason,
                        },
                        name
                    } = hhm;

                    const filerName: string = normalizeNameOfPerson(name);

                    if(incomeExplanationRequiredIndicator) {
                        return [
                            ...hhmsWithTaxHouseholdDiscrepancies,
                            <Fragment key = { `tax_household_discrepancy_${idx}` }>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.tax_household_income_discrepancy',
                                        dataInstructions: 'instructional_text.income_discrepancy',
                                        legendTextReplacements: {
                                            filerName: (
                                                <strong>
                                                    { filerName }
                                                </strong>
                                            ),
                                            taxHouseholdAnnualAmount: (
                                                <strong>
                                                    { currencyTools.getMoneyFormat(reportedIncome, true) }
                                                </strong>
                                            )
                                        }
                                    } }
                                    label = { {
                                        ulClass:[ 'margin-b-20' ],
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'taxHouseholdIncomeDifferenceReason',
                                        value: taxHouseholdIncomeDifferenceReason,
                                        options: getIncomeDiscrepancyFilteredOptions(
                                            isState.MN
                                                ? DROPDOWN_OPTIONS.INCOME_TAX_HOUSEHOLD_DISCREPANCIES_MN
                                                : DROPDOWN_OPTIONS.INCOME_TAX_HOUSEHOLD_DISCREPANCIES,
                                            stateCode,
                                            FREQUENCY_TYPES.YEARLY
                                        ),
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: hhm,
                                        fields: [ 'annualTaxIncome' ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onTaxHouseholdReasonChange
                                    } }
                                    indexInArray = { idx }
                                />
                            </Fragment>
                        ];
                    } else {
                        return hhmsWithTaxHouseholdDiscrepancies;
                    }
                },
                [ ]
            );
    };

    return (
        <Fragment>
            <div
                className = 'fade-in'
                id = 'iiIncomeDiscrepancy'
            >
                <div className = 'subsection' >
                    <Form { ...{ formName }}>
                        {
                            !isState.MN && getIndividualIncomeDiscrepancies()
                        }
                        {
                            getTaxHouseholdDiscrepancies()
                        }
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

export default Page3_38_E4;